import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Popconfirm,
  Select,
  Switch,
  Tag,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ProtocolEnum,
  organizationsOrganizationsCustomAuthProvidersCreateMutation,
  organizationsOrganizationsCustomAuthProvidersDestroyMutation,
  organizationsOrganizationsCustomAuthProvidersListOptions,
  organizationsOrganizationsCustomAuthProvidersListQueryKey,
  organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation,
  type OrganizationCustomAuthProvider,
  type OrganizationCustomAuthProviderWriteWritable
} from 'src/api/main'
import {
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { useShowApiErrors } from 'src/utils'

import {
  PROVIDER_LOGO_PRESETS,
  ProviderLogo,
  presetValue
} from './ProviderLogo'

const { Text, Title } = Typography

const PROTOCOLS: ProtocolEnum[] = [
  ProtocolEnum.OIDC,
  ProtocolEnum.OAUTH2,
  ProtocolEnum.SAML2
]

const MASKED_SECRET_PLACEHOLDER = '********'

type DrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; provider: OrganizationCustomAuthProvider }

type FormValues = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled: boolean
  logo_kind?: 'none' | 'preset' | 'url'
  logo_preset?: string
  logo_url?: string
  client_id?: string
  client_secret?: string
  issuer_url?: string
  authorize_url?: string
  token_url?: string
  userinfo_url?: string
  jwks_url?: string
  scope?: string
  saml_entity_id?: string
  saml_sso_url?: string
  saml_slo_url?: string
  saml_x509_cert?: string
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

const resolveLogoValue = (values: FormValues): string => {
  if (values.logo_kind === 'preset' && values.logo_preset) {
    return presetValue(values.logo_preset)
  }
  if (values.logo_kind === 'url') {
    return (values.logo_url ?? '').trim()
  }
  return ''
}

const buildPayload = (
  values: FormValues
): OrganizationCustomAuthProviderWriteWritable => {
  const payload: OrganizationCustomAuthProviderWriteWritable = {
    name: values.name,
    slug: values.slug,
    protocol: values.protocol,
    enabled: values.enabled,
    logo: resolveLogoValue(values),
    client_id: values.client_id || undefined,
    issuer_url: values.issuer_url || undefined,
    authorize_url: values.authorize_url || undefined,
    token_url: values.token_url || undefined,
    userinfo_url: values.userinfo_url || undefined,
    jwks_url: values.jwks_url || undefined,
    scope: values.scope || undefined,
    saml_entity_id: values.saml_entity_id || undefined,
    saml_sso_url: values.saml_sso_url || undefined,
    saml_slo_url: values.saml_slo_url || undefined,
    saml_x509_cert: values.saml_x509_cert || undefined,
    email_claim: values.email_claim || undefined,
    first_name_claim: values.first_name_claim || undefined,
    last_name_claim: values.last_name_claim || undefined
  }
  if (
    values.client_secret &&
    values.client_secret !== MASKED_SECRET_PLACEHOLDER
  ) {
    payload.client_secret = values.client_secret
  }
  return payload
}

type Props = {
  workspaceId: number
  idValid: boolean
}

export const WorkspaceCustomProvidersPanel = ({
  workspaceId,
  idValid
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [drawer, setDrawer] = useState<DrawerState>({ mode: 'closed' })
  const [form] = Form.useForm<FormValues>()
  const protocolWatch = Form.useWatch('protocol', form)
  const logoKindWatch = Form.useWatch('logo_kind', form)
  const logoPresetWatch = Form.useWatch('logo_preset', form)
  const logoUrlWatch = Form.useWatch('logo_url', form)

  const queryKey = organizationsOrganizationsCustomAuthProvidersListQueryKey({
    path: { id: workspaceId }
  })
  const {
    data: providers = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsOrganizationsCustomAuthProvidersListOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid,
    select: (response) => response.results
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey, exact: false })

  const createMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersCreateMutation(),
    onSuccess: () => {
      message.success(t('workspace.customProviders.messages.created'))
      void invalidate()
      setDrawer({ mode: 'closed' })
    },
    onError: (e) => showApiErrors(e)
  })

  const updateMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation(),
    onSuccess: () => {
      message.success(t('workspace.customProviders.messages.updated'))
      void invalidate()
      setDrawer({ mode: 'closed' })
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersDestroyMutation(),
    onSuccess: () => {
      message.success(t('workspace.customProviders.messages.deleted'))
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  const toggleMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation(),
    onSuccess: () => {
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  useEffect(() => {
    if (drawer.mode === 'create') {
      form.resetFields()
      form.setFieldsValue({
        protocol: ProtocolEnum.OIDC,
        enabled: false,
        logo_kind: 'none',
        scope: 'openid email profile',
        email_claim: 'email',
        first_name_claim: 'given_name',
        last_name_claim: 'family_name'
      })
    } else if (drawer.mode === 'edit') {
      const p = drawer.provider
      form.resetFields()
      let logo_kind: FormValues['logo_kind'] = 'none'
      let logo_preset = ''
      let logo_url = ''
      if (p.logo?.startsWith('preset:')) {
        logo_kind = 'preset'
        logo_preset = p.logo.slice('preset:'.length)
      } else if (p.logo) {
        logo_kind = 'url'
        logo_url = p.logo
      }
      form.setFieldsValue({
        name: p.name,
        slug: p.slug,
        protocol: p.protocol,
        enabled: p.enabled,
        logo_kind,
        logo_preset,
        logo_url,
        client_id: p.client_id ?? '',
        client_secret: p.client_secret ?? '', // already masked from server
        issuer_url: p.issuer_url ?? '',
        authorize_url: p.authorize_url ?? '',
        token_url: p.token_url ?? '',
        userinfo_url: p.userinfo_url ?? '',
        jwks_url: p.jwks_url ?? '',
        scope: p.scope,
        saml_entity_id: p.saml_entity_id ?? '',
        saml_sso_url: p.saml_sso_url ?? '',
        saml_slo_url: p.saml_slo_url ?? '',
        saml_x509_cert: p.saml_x509_cert ?? '',
        email_claim: p.email_claim,
        first_name_claim: p.first_name_claim,
        last_name_claim: p.last_name_claim
      })
    }
  }, [drawer, form])

  const columns: ColumnsType<OrganizationCustomAuthProvider> = useMemo(
    () => [
      {
        title: '',
        key: 'logo',
        fixed: 'left',
        width: 36,
        render: (_: unknown, record: OrganizationCustomAuthProvider) => (
          <ProviderLogo value={record.logo} size={20} />
        )
      },
      {
        fixed: 'left',
        title: t('workspace.customProviders.columns.name'),
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: t('workspace.customProviders.columns.slug'),
        dataIndex: 'slug',
        key: 'slug',
        render: (slug: string) => <Text code>{slug}</Text>
      },
      {
        title: t('workspace.customProviders.columns.protocol'),
        dataIndex: 'protocol',
        key: 'protocol',
        render: (protocol: ProtocolEnum) => (
          <Tag color="processing">
            {t(`workspace.customProviders.protocols.${protocol}`)}
          </Tag>
        )
      },
      {
        title: t('workspace.customProviders.columns.enabled'),
        key: 'enabled',
        render: (_: unknown, record: OrganizationCustomAuthProvider) => (
          <Switch
            checked={record.enabled}
            loading={
              toggleMutation.isPending &&
              toggleMutation.variables?.path?.provider_id === String(record.id)
            }
            onChange={(checked) =>
              toggleMutation.mutate({
                path: { id: workspaceId, provider_id: String(record.id) },
                body: { enabled: checked }
              })
            }
          />
        )
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 220,
        render: (_: unknown, record: OrganizationCustomAuthProvider) => (
          <Flex gap={8} wrap="wrap">
            <Button
              size="small"
              onClick={() => setDrawer({ mode: 'edit', provider: record })}
            >
              {t('workspace.customProviders.actions.edit')}
            </Button>
            <Popconfirm
              title={t('workspace.customProviders.deleteConfirm', {
                name: record.name
              })}
              onConfirm={() =>
                deleteMutation.mutate({
                  path: {
                    id: workspaceId,
                    provider_id: String(record.id)
                  }
                })
              }
              okButtonProps={{
                loading:
                  deleteMutation.isPending &&
                  deleteMutation.variables?.path?.provider_id ===
                    String(record.id)
              }}
            >
              <Button
                size="small"
                danger
                loading={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.path?.provider_id ===
                    String(record.id)
                }
              >
                {t('workspace.customProviders.actions.delete')}
              </Button>
            </Popconfirm>
          </Flex>
        )
      }
    ],
    [t, toggleMutation, deleteMutation, workspaceId]
  )

  const onSubmit = (values: FormValues) => {
    const body = buildPayload(values)
    if (drawer.mode === 'edit') {
      updateMutation.mutate({
        path: { id: workspaceId, provider_id: String(drawer.provider.id) },
        body
      })
    } else {
      createMutation.mutate({
        path: { id: workspaceId },
        body
      })
    }
  }

  const submitting = createMutation.isPending || updateMutation.isPending

  return (
    <Flex orientation="vertical" gap={16} style={{ width: '100%' }}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}
      <ProTableContainer<OrganizationCustomAuthProvider>
        showSearch={false}
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={providers}
        locale={{
          emptyText: (
            <Empty description={t('workspace.customProviders.empty')} />
          )
        }}
        pagination={false}
        leftChildren={
          <Flex vertical gap={2}>
            <Text strong>{t('workspace.customProviders.title')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.customProviders.description')}
            </Text>
          </Flex>
        }
        rightChildren={
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={() => setDrawer({ mode: 'create' })}
          >
            {t('workspace.customProviders.addProvider')}
          </Button>
        }
      />

      <Drawer
        title={
          drawer.mode === 'edit'
            ? t('workspace.customProviders.editProvider')
            : t('workspace.customProviders.addProvider')
        }
        open={drawer.mode !== 'closed'}
        onClose={() => setDrawer({ mode: 'closed' })}
        size={520}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setDrawer({ mode: 'closed' })}>
              {t('cancel')}
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={() => form.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form<FormValues> form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="name"
            label={t('workspace.customProviders.fields.name')}
            rules={[{ required: true }]}
          >
            <Input placeholder="Auth0 production" />
          </Form.Item>
          <Form.Item
            name="slug"
            label={t('workspace.customProviders.fields.slug')}
            rules={[
              { required: true },
              {
                pattern: /^[a-z][a-z0-9_-]{1,62}$/,
                message: t('workspace.customProviders.fields.slugInvalid')
              }
            ]}
            extra={t('workspace.customProviders.fields.slugHelp')}
          >
            <Input placeholder="auth0-prod" disabled={drawer.mode === 'edit'} />
          </Form.Item>
          <Form.Item
            name="protocol"
            label={t('workspace.customProviders.fields.protocol')}
            rules={[{ required: true }]}
          >
            <Select
              options={PROTOCOLS.map((p) => ({
                value: p,
                label: t(`workspace.customProviders.protocols.${p}`)
              }))}
              disabled={drawer.mode === 'edit'}
            />
          </Form.Item>
          <Form.Item
            name="enabled"
            label={t('workspace.customProviders.fields.enabled')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="logo_kind"
            label={t('workspace.customProviders.fields.logo')}
          >
            <Select
              options={[
                {
                  value: 'none',
                  label: t('workspace.customProviders.fields.logoNone')
                },
                {
                  value: 'preset',
                  label: t('workspace.customProviders.fields.logoPreset')
                },
                {
                  value: 'url',
                  label: t('workspace.customProviders.fields.logoUrl')
                }
              ]}
            />
          </Form.Item>

          {logoKindWatch === 'preset' ? (
            <Form.Item
              name="logo_preset"
              label={t('workspace.customProviders.fields.logoPresetPick')}
              rules={[{ required: true }]}
            >
              <Select
                options={PROVIDER_LOGO_PRESETS.map((key) => ({
                  value: key,
                  label: (
                    <Flex align="center" gap={8}>
                      <ProviderLogo value={presetValue(key)} size={16} />
                      <span style={{ textTransform: 'capitalize' }}>{key}</span>
                    </Flex>
                  )
                }))}
              />
            </Form.Item>
          ) : null}

          {logoKindWatch === 'url' ? (
            <Form.Item
              name="logo_url"
              label={t('workspace.customProviders.fields.logoUrlInput')}
              extra={t('workspace.customProviders.fields.logoUrlHelp')}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input placeholder="https://example.com/logo.svg" />
            </Form.Item>
          ) : null}

          {logoKindWatch && logoKindWatch !== 'none' ? (
            <Flex
              align="center"
              gap={8}
              style={{ marginBottom: 16, marginLeft: 4 }}
            >
              <Text type="secondary">
                {t('workspace.customProviders.fields.logoPreview')}
              </Text>
              <ProviderLogo
                value={
                  logoKindWatch === 'preset'
                    ? logoPresetWatch
                      ? presetValue(logoPresetWatch)
                      : ''
                    : (logoUrlWatch ?? '')
                }
                size={24}
              />
            </Flex>
          ) : null}

          <Title level={5} style={{ marginTop: 16 }}>
            {t('workspace.customProviders.sections.connection')}
          </Title>

          {protocolWatch === ProtocolEnum.OIDC ||
          protocolWatch === ProtocolEnum.OAUTH2 ? (
            <>
              <Form.Item
                name="client_id"
                label={t('workspace.customProviders.fields.clientId')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="client_secret"
                label={t('workspace.customProviders.fields.clientSecret')}
                extra={t('workspace.customProviders.fields.clientSecretHelp')}
              >
                <Input.Password
                  placeholder={
                    drawer.mode === 'edit' ? MASKED_SECRET_PLACEHOLDER : ''
                  }
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item
                name="scope"
                label={t('workspace.customProviders.fields.scope')}
              >
                <Input />
              </Form.Item>
            </>
          ) : null}

          {protocolWatch === ProtocolEnum.OIDC ? (
            <>
              <Form.Item
                name="issuer_url"
                label={t('workspace.customProviders.fields.issuerUrl')}
                extra={t('workspace.customProviders.fields.issuerUrlHelp')}
                rules={[{ required: true }]}
              >
                <Input placeholder="https://example.auth0.com/" />
              </Form.Item>
              <Form.Item
                name="authorize_url"
                label={t(
                  'workspace.customProviders.fields.authorizeUrlOptional'
                )}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="token_url"
                label={t('workspace.customProviders.fields.tokenUrlOptional')}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="userinfo_url"
                label={t(
                  'workspace.customProviders.fields.userinfoUrlOptional'
                )}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="jwks_url"
                label={t('workspace.customProviders.fields.jwksUrlOptional')}
              >
                <Input />
              </Form.Item>
            </>
          ) : null}

          {protocolWatch === ProtocolEnum.OAUTH2 ? (
            <>
              <Form.Item
                name="authorize_url"
                label={t('workspace.customProviders.fields.authorizeUrl')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="token_url"
                label={t('workspace.customProviders.fields.tokenUrl')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="userinfo_url"
                label={t('workspace.customProviders.fields.userinfoUrl')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </>
          ) : null}

          {protocolWatch === ProtocolEnum.SAML2 ? (
            <>
              <Form.Item
                name="saml_entity_id"
                label={t('workspace.customProviders.fields.samlEntityId')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="saml_sso_url"
                label={t('workspace.customProviders.fields.samlSsoUrl')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="saml_slo_url"
                label={t('workspace.customProviders.fields.samlSloUrl')}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="saml_x509_cert"
                label={t('workspace.customProviders.fields.samlX509Cert')}
                rules={[{ required: true }]}
                extra={t('workspace.customProviders.fields.samlX509CertHelp')}
              >
                <Input.TextArea
                  rows={6}
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
              </Form.Item>
            </>
          ) : null}

          <Title level={5} style={{ marginTop: 16 }}>
            {t('workspace.customProviders.sections.claimMapping')}
          </Title>
          <Form.Item
            name="email_claim"
            label={t('workspace.customProviders.fields.emailClaim')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="first_name_claim"
            label={t('workspace.customProviders.fields.firstNameClaim')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="last_name_claim"
            label={t('workspace.customProviders.fields.lastNameClaim')}
          >
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </Flex>
  )
}
